import { useState, useEffect, useRef } from "react";

const boxes = [
  { col: "1 / 4", row: "1 / 2", cx: 2, cy: 1, from: "top", title: "Dashboard", desc: "Overview of all your key metrics and analytics at a glance." },
  { col: "4 / 7", row: "1 / 3", cx: 5.5, cy: 1.5, from: "top", title: "Analytics", desc: "Deep dive into user behavior, trends, and performance data." },
  { col: "7 / 8", row: "1 / 2", cx: 7.5, cy: 1, from: "top", title: "Alerts", desc: "Real-time notifications for critical events." },
  { col: "8 / 9", row: "1 / 2", cx: 8.5, cy: 1, from: "top", title: "Status", desc: "Live system health and uptime monitoring." },
  { col: "9 / 13", row: "1 / 2", cx: 11, cy: 1, from: "top", title: "Navigation", desc: "Quick access to all sections and tools in the platform." },
  { col: "1 / 4", row: "2 / 4", cx: 2, cy: 2.5, from: "left", title: "Projects", desc: "Manage and track all active projects and their progress." },
  { col: "7 / 9", row: "2 / 3", cx: 8, cy: 2.5, from: "right", title: "Calendar", desc: "Schedule events, deadlines, and milestones." },
  { col: "9 / 11", row: "2 / 3", cx: 10, cy: 2.5, from: "right", title: "Messages", desc: "Team communication and direct messaging hub." },
  { col: "11 / 13", row: "2 / 3", cx: 12, cy: 2.5, from: "right", title: "Settings", desc: "Configure preferences, integrations, and permissions." },
  { col: "4 / 6", row: "3 / 5", cx: 5, cy: 3.5, from: "left", title: "Reports", desc: "Generate and export detailed performance reports." },
  { col: "6 / 9", row: "3 / 6", cx: 7.5, cy: 4, from: "bottom", title: "Workspace", desc: "Your central hub for collaboration and content creation." },
  { col: "9 / 10", row: "3 / 4", cx: 9.5, cy: 3.5, from: "right", title: "Tags", desc: "Organize content with labels and categories." },
  { col: "10 / 13", row: "3 / 6", cx: 11.5, cy: 4, from: "right", title: "Media Library", desc: "Browse, upload, and manage all your media assets." },
  { col: "1 / 2", row: "4 / 6", cx: 1, cy: 5, from: "left", title: "Profile", desc: "View and edit your personal information." },
  { col: "2 / 4", row: "4 / 6", cx: 3, cy: 5, from: "left", title: "Teams", desc: "Manage team members, roles, and access levels." },
  { col: "4 / 6", row: "5 / 8", cx: 5, cy: 6, from: "bottom", title: "Files", desc: "Cloud storage for documents, images, and resources." },
  { col: "9 / 10", row: "4 / 6", cx: 9.5, cy: 5, from: "right", title: "Filters", desc: "Narrow down results with advanced filtering." },
  { col: "1 / 4", row: "6 / 8", cx: 2, cy: 7, from: "bottom", title: "Activity", desc: "Timeline of recent actions and changes across the platform." },
  { col: "6 / 7", row: "6 / 8", cx: 6.5, cy: 7, from: "bottom", title: "Notes", desc: "Quick notes and bookmarks for later." },
  { col: "7 / 8", row: "6 / 8", cx: 7.5, cy: 7, from: "bottom", title: "Pins", desc: "Pinned items for fast access." },
  { col: "8 / 11", row: "6 / 8", cx: 9.5, cy: 7, from: "bottom", title: "Integrations", desc: "Connect third-party tools and services seamlessly." },
  { col: "11 / 13", row: "6 / 8", cx: 12, cy: 7, from: "bottom", title: "Help", desc: "Documentation, FAQs, and support resources." },
];

const centerX = 6.5, centerY = 4;
const maxDist = Math.max(...boxes.map(b => Math.sqrt((b.cx - centerX) ** 2 + (b.cy - centerY) ** 2)));
const enriched = boxes.map(b => ({
  ...b,
  normDist: Math.sqrt((b.cx - centerX) ** 2 + (b.cy - centerY) ** 2) / maxDist
}));

function getOffset(from) {
  switch (from) {
    case "top": return [0, -1];
    case "bottom": return [0, 1];
    case "left": return [-1, 0];
    case "right": return [1, 0];
    default: return [0, 0];
  }
}

const TW = 240, TH = 200, GAP = 14;

const badges = ["New", "Beta", "Pro", "Popular", "Updated", "Core", "Essential", "v2.0", "Hot", "AI", "Live"];
const gradients = [
  "linear-gradient(135deg, #667eea, #764ba2)",
  "linear-gradient(135deg, #f093fb, #f5576c)",
  "linear-gradient(135deg, #4facfe, #00f2fe)",
  "linear-gradient(135deg, #43e97b, #38f9d7)",
  "linear-gradient(135deg, #fa709a, #fee140)",
  "linear-gradient(135deg, #a18cd1, #fbc2eb)",
  "linear-gradient(135deg, #fccb90, #d57eeb)",
  "linear-gradient(135deg, #e0c3fc, #8ec5fc)",
  "linear-gradient(135deg, #f77062, #fe5196)",
  "linear-gradient(135deg, #89f7fe, #66a6ff)",
  "linear-gradient(135deg, #fddb92, #d1fdff)",
];
const badgeColors = ["#764ba2", "#f5576c", "#00b4d8", "#38f9d7", "#fa709a", "#a18cd1", "#d57eeb", "#4facfe", "#fe5196", "#66a6ff", "#fddb92"];

export default function AboutPassions() {
  const [progress, setProgress] = useState(0);
  const [hover, setHover] = useState(null);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const scrollAccum = useRef(0);
  const displayProgress = useRef(0);
  const rafId = useRef(null);
  const maxScroll = 3000;
  const lerp = 0.06;

  useEffect(() => {
    const animate = () => {
      const diff = scrollAccum.current / maxScroll - displayProgress.current;
      if (Math.abs(diff) > 0.0005) {
        displayProgress.current += diff * lerp;
        setProgress(displayProgress.current);
      }
      rafId.current = requestAnimationFrame(animate);
    };
    rafId.current = requestAnimationFrame(animate);

    const onWheel = (e) => {
      e.preventDefault();
      scrollAccum.current = Math.max(0, Math.min(maxScroll, scrollAccum.current + e.deltaY));
    };

    let lastTouchY = null;
    const onTouchStart = (e) => { lastTouchY = e.touches[0].clientY; };
    const onTouchMove = (e) => {
      if (lastTouchY === null) return;
      e.preventDefault();
      const delta = lastTouchY - e.touches[0].clientY;
      lastTouchY = e.touches[0].clientY;
      scrollAccum.current = Math.max(0, Math.min(maxScroll, scrollAccum.current + delta * 2));
    };
    const onTouchEnd = () => { lastTouchY = null; };

    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      cancelAnimationFrame(rafId.current);
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, []);

  const onMouseMove = (e) => {
    setMouse({ x: e.clientX, y: e.clientY });
  };

  // Calculate tooltip position to stay on screen
  const ww = typeof window !== 'undefined' ? window.innerWidth : 1000;
  const wh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const tipX = mouse.x + GAP + TW > ww ? mouse.x - GAP - TW : mouse.x + GAP;
  const tipY = mouse.y + GAP + TH > wh ? mouse.y - GAP - TH : mouse.y + GAP;

  return (
    <div
      onMouseMove={onMouseMove}
      style={{
        height: '100vh',
        backgroundColor: '#1a1a1a',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px',
        overflow: 'hidden',
        position: 'relative',
        cursor: 'default',
      }}
    >
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(12, 1fr)',
        gridTemplateRows: 'repeat(10, 55px)',
        gap: '12px',
        width: '100%',
        maxWidth: '1100px',
      }}>
        {enriched.map((b, i) => {
          const start = b.normDist * 0.5;
          const end = start + 0.5;
          const t = Math.max(0, Math.min(1, (progress - start) / (end - start)));
          const eased = 1 - Math.pow(1 - t, 3);
          const [ox, oy] = getOffset(b.from);
          const d = 120;
          const tx = ox * d * (1 - eased);
          const ty = oy * d * (1 - eased);
          const isHovered = hover === i;

          return (
            <div
              key={i}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              style={{
                gridColumn: b.col,
                gridRow: b.row,
                backgroundColor: isHovered ? '#353535' : '#2a2a2a',
                borderRadius: '12px',
                border: `1px solid ${isHovered ? '#4a4a4a' : '#333'}`,
                transform: `translate(${tx}vw, ${ty}vh)`,
                opacity: eased,
                willChange: 'transform, opacity',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease, border-color 0.2s ease',
              }}
            />
          );
        })}
      </div>

      {/* Tooltip */}
      {hover !== null && (
        <div style={{
          position: 'fixed',
          left: tipX,
          top: tipY,
          width: TW,
          backgroundColor: '#1e1e1e',
          border: '1px solid #3a3a3a',
          borderRadius: '14px',
          pointerEvents: 'none',
          zIndex: 1000,
          boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
          overflow: 'hidden',
          fontFamily: 'system-ui, sans-serif',
        }}>
          {/* Image header */}
          <div style={{
            width: '100%',
            height: 120,
            background: gradients[hover % gradients.length],
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <div style={{
              fontSize: '36px',
              color: 'rgba(255,255,255,0.3)',
              fontWeight: 800,
              letterSpacing: '2px',
              textTransform: 'uppercase',
            }}>
              {enriched[hover].title.slice(0, 2)}
            </div>
            {/* Decorative circles */}
            <div style={{ position: 'absolute', top: 14, right: 14, width: 30, height: 30, borderRadius: '50%', background: 'rgba(255,255,255,0.15)' }} />
            <div style={{ position: 'absolute', bottom: 20, left: 18, width: 16, height: 16, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
          </div>
          {/* Bottom content */}
          <div style={{ padding: '14px 16px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
              <div style={{ color: '#eee', fontSize: '15px', fontWeight: 700 }}>
                {enriched[hover].title}
              </div>
              <div style={{
                fontSize: '10px',
                fontWeight: 700,
                color: '#fff',
                backgroundColor: badgeColors[hover % badgeColors.length],
                padding: '2px 8px',
                borderRadius: '20px',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
                lineHeight: '18px',
              }}>
                {badges[hover % badges.length]}
              </div>
            </div>
            <div style={{ color: '#777', fontSize: '12px', lineHeight: '1.5' }}>
              {enriched[hover].desc}
            </div>
          </div>
        </div>
      )}

      {/* Scroll indicator */}
      <div style={{
        position: 'fixed',
        bottom: '24px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
        opacity: progress < 0.95 ? 0.5 : 0,
        transition: 'opacity 0.3s ease',
        pointerEvents: 'none',
      }}>
        <span style={{ color: '#555', fontSize: '13px', letterSpacing: '1px', fontFamily: 'system-ui, sans-serif' }}>SCROLL</span>
        <div style={{
          width: '1px',
          height: '24px',
          background: 'linear-gradient(to bottom, #555, transparent)',
        }} />
      </div>

      {/* Progress bar */}
      <div style={{
        position: 'fixed',
        right: '20px',
        top: '50%',
        transform: 'translateY(-50%)',
        width: '3px',
        height: '80px',
        backgroundColor: '#2a2a2a',
        borderRadius: '2px',
      }}>
        <div style={{
          width: '100%',
          height: `${progress * 100}%`,
          backgroundColor: '#555',
          borderRadius: '2px',
          transition: 'height 0.1s ease-out',
        }} />
      </div>
    </div>
  );
}